import Seo from "@/components/Seo";
import { Link, useParams } from "react-router-dom";
import { SERVICES, getService } from "@/lib/content";
import { SERVICE_FAQS } from "@/data/serviceFaqs";
import { ArrowUpRight, Check, HelpCircle } from "lucide-react";
import VideoTile from "@/components/VideoTile";
import BottomCTA from "@/components/BottomCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Per-service pun for the bottom-CTA headline. Falls back to "…that lands?"
// only if we haven't authored a bespoke one.
const CTA_PUNS = {
  "drone-filming": "Want drone filming that lands?",
  "ground-filming": "Want ground filming that rolls?",
  photography: "Want photography that frames it right?",
  editing: "Want editing that cuts through?",
  cgi: "Want CGI that renders it real?",
  "content-writing": "Want writing that actually gets read?",
  "social-media": "Want social that stops the scroll?",
  "website-design": "Want a website that clicks?",
  "graphic-design": "Want design that pops?",
  "industry-reach": "Want reach that travels?",
  "audience-growth": "Want an audience that grows on you?",
  "brand-development": "Want a brand that sticks?",
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = getService(slug);

  if (!s) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <p className="label-eyebrow text-accent-blue">404</p>
          <h1 className="font-heading font-black text-4xl mt-4">
            Service not found.
          </h1>
          <Link
            to="/what-we-do"
            className="mt-6 inline-flex text-accent-blue text-sm font-bold"
          >
            ← All services
          </Link>
        </div>
      </main>
    );
  }

  const faqPack = SERVICE_FAQS[s.slug];
  const relatedSlugs =
    faqPack && faqPack.relatedServices
      ? faqPack.relatedServices
      : SERVICES.filter((x) => x.slug !== s.slug)
          .slice(0, 3)
          .map((x) => x.slug);
  const others = relatedSlugs
    .map((sl) => SERVICES.find((x) => x.slug === sl))
    .filter(Boolean);

  return (
    <main
      data-testid={`service-page-${s.slug}`}
      className="bg-zinc-950 pt-20"
    >
      <Seo
        title={`${s.label} for highways & infrastructure`}
        description={`${s.label} services for the UK highways and infrastructure sector, from HighwaysIndustry.com. ${s.blurb || ""}`.trim()}
        path={`/services/${s.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": s.label,
          "provider": {
            "@type": "Organization",
            "name": "HighwaysIndustry.com",
            "url": "https://whyhighwaysindustry.com",
          },
          "areaServed": { "@type": "Country", "name": "United Kingdom" },
          "serviceType": s.label,
          "description": s.blurb || "",
          "url": `https://whyhighwaysindustry.com/services/${s.slug}`,
        }}
      />
      {/* Hero */}
      <section className="dark-section relative border-b border-zinc-800 overflow-hidden">
        <div className="absolute inset-0">
          <VideoTile video={s.video} poster={s.image} alt="" overlay={false} />
        </div>
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
          <Link
            to="/what-we-do"
            className="label-eyebrow text-accent-blue inline-flex items-center gap-2"
          >
            ← All Services
          </Link>
          <h1
            data-testid="service-headline"
            className="font-heading font-black text-5xl md:text-7xl lg:text-8xl mt-6 max-w-5xl leading-[0.95] tracking-tighter text-white"
          >
            {s.label}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            {s.short}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/positioning-review"
              data-testid="service-cta-discovery"
              className="bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 hover:glow-blue transition-all rounded-full"
            >
              Book A Discovery Call <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="border border-white/30 hover:border-accent-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 transition-colors rounded-full"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="border-b border-zinc-900 py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">Overview</span>
            </div>
            <h2 className="font-heading font-black text-3xl md:text-4xl mt-4 leading-tight tracking-tight">
              What you get.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-zinc-300 text-lg md:text-xl leading-relaxed">
              {s.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Why it matters, only renders if service defines it */}
      {s.whyItMatters && (
        <section
          data-testid={`service-why-${s.slug}`}
          className="border-b border-zinc-900 py-20 md:py-28 bg-slate-50 relative overflow-hidden"
        >
          <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Why it matters
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl mt-5 leading-[0.95] tracking-tighter text-slate-900">
                {s.whyItMatters.headline}
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6 space-y-6">
              {s.whyItMatters.body.map((p, i) => (
                <p
                  key={i}
                  className="text-lg md:text-xl text-slate-700 leading-relaxed"
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery, two-image feature strip (only renders if service has a gallery) */}
      {s.gallery && s.gallery.length >= 2 && (
        <section
          data-testid={`service-gallery-${s.slug}`}
          className="border-b border-zinc-900 py-24 md:py-32 bg-zinc-950 relative overflow-hidden"
        >
          <div className="absolute inset-0 blueprint-dots opacity-20" />
          <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="grid lg:grid-cols-12 gap-6 items-end mb-14">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3">
                  <span className="h-px w-12 bg-accent-blue" />
                  <span className="label-eyebrow text-accent-blue">
                    Recent Work
                  </span>
                </div>
                <h2 className="font-heading font-black text-4xl md:text-5xl mt-4 leading-[0.95] tracking-tighter text-white">
                  On the ground.
                  <br />
                  <span className="text-accent-blue italic">In the field.</span>
                </h2>
              </div>
              <div className="lg:col-span-4">
                <p className="text-zinc-400 leading-relaxed">
                  A recent look at what this service delivers for highways
                  clients, captured on live jobs.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {/* Image 1 */}
              <div className="relative group">
                <span className="absolute -top-3 -left-3 z-10 inline-flex items-center justify-center bg-accent-blue text-black font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full">
                  01 · {s.gallery[0].badge}
                </span>
                <div className="overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
                  <img
                    src={s.gallery[0].src}
                    alt={s.gallery[0].alt}
                    data-testid={`gallery-image-1`}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>

              {/* Image 2 */}
              <div className="relative group">
                <span className="absolute -top-3 -left-3 z-10 inline-flex items-center justify-center bg-white text-black font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full">
                  02 · {s.gallery[1].badge}
                </span>
                <div className="overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl">
                  <img
                    src={s.gallery[1].src}
                    alt={s.gallery[1].alt}
                    data-testid={`gallery-image-2`}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Deliverables */}
      <section className="border-b border-zinc-900 py-20 md:py-28 bg-black">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Deliverables
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-5xl mt-4 leading-[0.95] tracking-tighter max-w-2xl">
                Exactly what we ship.
              </h2>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {s.deliverables.map((d) => (
              <div
                key={d}
                className="bg-zinc-950 p-6 md:p-7 flex items-start gap-4"
                data-testid={`deliverable-${d.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
              >
                <Check className="h-5 w-5 text-accent-blue mt-1 flex-shrink-0" />
                <span className="text-base md:text-lg font-medium leading-snug">
                  {d}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service FAQs, knowledge-graph layer */}
      {faqPack && faqPack.items && faqPack.items.length > 0 && (
        <section
          data-testid={`service-faqs-${s.slug}`}
          className="py-20 md:py-28 bg-slate-50 border-b border-slate-200"
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-16">
            <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  {faqPack.eyebrow}
                </span>
              </div>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter text-slate-900">
                {faqPack.heading}
              </h2>
              <p className="mt-4 text-sm text-slate-500">
                {faqPack.items.length} common questions, answered honestly.
              </p>
            </div>
            <div className="lg:col-span-8">
              <Accordion
                type="single"
                collapsible
                className="w-full"
                data-testid={`service-faq-accordion-${s.slug}`}
              >
                {faqPack.items.map((item, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`${s.slug}-${idx}`}
                    className="border-b border-slate-200 last:border-b-0"
                  >
                    <AccordionTrigger
                      data-testid={`service-faq-q-${s.slug}-${idx}`}
                      className="text-left py-5 hover:no-underline group"
                    >
                      <span className="flex items-start gap-3 pr-4">
                        <HelpCircle
                          size={18}
                          className="text-accent-blue mt-0.5 flex-shrink-0"
                          strokeWidth={2}
                        />
                        <span className="font-heading font-bold text-base md:text-lg text-slate-900 group-hover:text-accent-blue transition-colors">
                          {item.q}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5">
                      <div className="pl-8 space-y-3 text-slate-700 text-base md:text-lg leading-relaxed">
                        {(Array.isArray(item.a) ? item.a : [item.a]).map(
                          (block, bIdx) =>
                            typeof block === "string" ? (
                              <p key={bIdx}>{block}</p>
                            ) : block && block.list ? (
                              <ul
                                key={bIdx}
                                className="list-disc pl-5 space-y-1 marker:text-accent-blue"
                              >
                                {block.list.map((li, lIdx) => (
                                  <li key={lIdx}>{li}</li>
                                ))}
                              </ul>
                            ) : null
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2 text-sm font-heading font-semibold text-accent-blue hover:text-cyan-500 transition-colors"
                >
                  See all FAQs
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other services */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              Related services
            </span>
          </div>
          <h2 className="font-heading font-black text-3xl md:text-4xl mt-4 tracking-tight">
            Pairs well with.
          </h2>
          <div className={`mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800`}>
            {others.map((x) => (
              <Link
                key={x.slug}
                to={`/services/${x.slug}`}
                data-testid={`service-related-${x.slug}`}
                className="bg-white hover:bg-slate-50 hover:shadow-md p-6 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-accent-blue">
                    SERVICE
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-accent-blue transition-colors" />
                </div>
                <p className="font-heading font-bold text-2xl mt-6 tracking-tight">
                  {x.label}
                </p>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                  {x.short}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach cross-link */}
      <section
        data-testid="service-our-approach-link"
        className="bg-slate-50 border-y border-slate-200 py-16 md:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Link
            to="/our-approach"
            className="group flex flex-col md:flex-row md:items-center gap-6 md:gap-10 rounded-2xl border border-slate-200 bg-white hover:border-accent-blue hover:shadow-md transition-all p-6 md:p-10"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Our approach to marketing
                </span>
              </div>
              <h3 className="mt-4 font-heading font-black text-2xl md:text-4xl leading-tight tracking-tight text-slate-900">
                Wondering why we recommend long-term
                <br />
                <span className="text-accent-blue italic">
                  campaigns over one-off marketing?
                </span>
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                Read our approach. It explains how branding creates the
                recognition, credibility and trust that wins frameworks,
                tenders and long-term contracts in this sector.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-900 group-hover:bg-slate-900 group-hover:text-white text-slate-900 px-6 py-3 text-sm font-heading font-semibold transition-colors flex-shrink-0">
              Read our approach
              <ArrowUpRight className="h-4 w-4" />
            </div>
          </Link>
        </div>
      </section>

      <BottomCTA
        headline={
          CTA_PUNS[s.slug] || `Want ${s.label.toLowerCase()} that lands?`
        }
        subline="Book a Positioning Review to map it out properly, or send us a message and we'll be in touch."
      />
    </main>
  );
}
